import React, { useEffect, useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../utils/api';
import { motion } from 'framer-motion';
import { Brain, Award, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export default function QuizPage() {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const response = await api.getQuizQuestions();
      setQuestions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar questões');
    } finally {
      setLoading(false);
    }
  };

  const startGame = () => {
    setGameStarted(true);
    setStartTime(Date.now());
    setCurrentIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
  };

  const handleAnswer = async (answerIndex) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answerIndex);
    const currentQuestion = questions[currentIndex];
    const isCorrect = answerIndex === currentQuestion.resposta_correta;
    const tempoResposta = Math.floor((Date.now() - startTime) / 1000);

    try {
      const result = await api.submitQuiz({
        question_id: currentQuestion.id,
        resposta_usuario: answerIndex,
        tempo_resposta: tempoResposta
      });

      if (result.data.correta) {
        setScore(score + result.data.pontos_ganhos);
        toast.success(`Correto! +${result.data.pontos_ganhos} pontos`);
      } else {
        toast.error('Resposta incorreta');
      }

      setTimeout(() => {
        if (currentIndex < questions.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setSelectedAnswer(null);
          setStartTime(Date.now());
        } else {
          setShowResult(true);
        }
      }, 1500);
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen p-4">
          <Brain className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Nenhuma questão disponível</h2>
          <p className="text-muted-foreground">As questões estarão disponíveis em breve!</p>
        </div>
      </Layout>
    );
  }

  if (!gameStarted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-md w-full"
          >
            <div className="text-center p-8 rounded-3xl bg-white border border-border shadow-xl">
              <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary to-secondary mx-auto mb-6 flex items-center justify-center">
                <Brain className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Quiz Interativo</h1>
              <p className="text-muted-foreground mb-8">
                Teste seus conhecimentos com {questions.length} questões selecionadas!
              </p>
              <Button
                onClick={startGame}
                data-testid="quiz-start-button"
                className="w-full h-12 rounded-full font-semibold text-base bg-gradient-to-r from-primary to-secondary"
              >
                Iniciar Quiz
              </Button>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  if (showResult) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-md w-full"
          >
            <div className="text-center p-8 rounded-3xl bg-white border border-border shadow-xl">
              <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary to-secondary mx-auto mb-6 flex items-center justify-center">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Quiz Concluído!</h1>
              <div className="mb-8">
                <p className="text-5xl font-bold text-primary mb-2">{score}</p>
                <p className="text-muted-foreground">Pontos totais</p>
              </div>
              <Button
                onClick={startGame}
                data-testid="quiz-restart-button"
                className="w-full h-12 rounded-full font-semibold text-base bg-gradient-to-r from-primary to-secondary"
              >
                Jogar Novamente
              </Button>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progressWidth = ((currentIndex + 1) / questions.length) * 100;

  return (
    <Layout>
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-muted">
          <div 
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
            style={{ width: `${progressWidth}%` }}
          />
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="p-8 rounded-3xl bg-white border border-border shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Brain className="h-4 w-4" />
                  <span>Questão {currentIndex + 1} de {questions.length}</span>
                </div>
                <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                  {currentQuestion.pontos} pts
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {currentQuestion.pergunta}
              </h2>

              <div className="space-y-3">
                {currentQuestion.opcoes && currentQuestion.opcoes.length > 0 && currentQuestion.opcoes[0] && (
                  <QuizOption
                    opcao={currentQuestion.opcoes[0]}
                    index={0}
                    selectedAnswer={selectedAnswer}
                    correctAnswer={currentQuestion.resposta_correta}
                    onClick={handleAnswer}
                  />
                )}
                {currentQuestion.opcoes && currentQuestion.opcoes.length > 1 && currentQuestion.opcoes[1] && (
                  <QuizOption
                    opcao={currentQuestion.opcoes[1]}
                    index={1}
                    selectedAnswer={selectedAnswer}
                    correctAnswer={currentQuestion.resposta_correta}
                    onClick={handleAnswer}
                  />
                )}
                {currentQuestion.opcoes && currentQuestion.opcoes.length > 2 && currentQuestion.opcoes[2] && (
                  <QuizOption
                    opcao={currentQuestion.opcoes[2]}
                    index={2}
                    selectedAnswer={selectedAnswer}
                    correctAnswer={currentQuestion.resposta_correta}
                    onClick={handleAnswer}
                  />
                )}
                {currentQuestion.opcoes && currentQuestion.opcoes.length > 3 && currentQuestion.opcoes[3] && (
                  <QuizOption
                    opcao={currentQuestion.opcoes[3]}
                    index={3}
                    selectedAnswer={selectedAnswer}
                    correctAnswer={currentQuestion.resposta_correta}
                    onClick={handleAnswer}
                  />
                )}
              </div>

              <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  <span>Pontos: {score}</span>
                </div>
                <div className="px-3 py-1 rounded-full bg-muted capitalize">
                  {currentQuestion.dificuldade}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function QuizOption({ opcao, index, selectedAnswer, correctAnswer, onClick }) {
  const isSelected = selectedAnswer === index;
  const isCorrect = index === correctAnswer;
  const showCorrect = selectedAnswer !== null && isCorrect;
  const showWrong = selectedAnswer !== null && isSelected && !isCorrect;

  return (
    <button
      data-testid={`quiz-option-${index}`}
      onClick={() => onClick(index)}
      disabled={selectedAnswer !== null}
      className={`w-full p-4 rounded-xl text-left font-medium transition-all ${
        showCorrect
          ? 'bg-green-100 border-2 border-green-500 text-green-700'
          : showWrong
          ? 'bg-red-100 border-2 border-red-500 text-red-700'
          : 'bg-muted border-2 border-transparent hover:border-primary'
      }`}
    >
      <div className="flex items-center justify-between">
        <span>{opcao}</span>
        {showCorrect && <Check className="h-5 w-5" />}
        {showWrong && <X className="h-5 w-5" />}
      </div>
    </button>
  );
}
